import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  MenuItem,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BRAND = "#069d6e";
const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

const apiBase = () => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
};

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [toast, setToast] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formEmail, setFormEmail] = useState("");
  const [formGroup, setFormGroup] = useState("");
  const [saving, setSaving] = useState(false);

  const loadGroups = async () => {
    try {
      const { data } = await axios.get(`${apiBase()}/contacts/groups`);
      setGroups(data?.groups || []);
    } catch (e) {
      console.error("Groups load error:", e);
    }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (groupFilter && groupFilter !== "all") params.group = groupFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get(`${apiBase()}/contacts`, { params });
      setContacts(data?.contacts || []);
    } catch (e) {
      console.error("Contacts load error:", e);
      setContacts([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    const t = setTimeout(loadContacts, 300);
    return () => clearTimeout(t);
  }, [search, groupFilter]);

  const openAdd = () => {
    setEditing(null);
    setFormEmail("");
    setFormGroup("");
    setDialogOpen(true);
  };

  const openEdit = (contact) => {
    setEditing(contact);
    setFormEmail(contact.email || "");
    setFormGroup(contact.group || "");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const saveContact = async () => {
    if (!emailRegex.test(formEmail.trim())) {
      setToast({ type: "error", msg: "Please enter a valid email." });
      return;
    }
    setSaving(true);
    const payload = { email: formEmail.trim(), group: formGroup.trim() || null };
    try {
      if (editing) {
        await axios.put(`${apiBase()}/contacts/${editing.id}`, payload);
        setToast({ type: "success", msg: "Contact updated." });
      } else {
        await axios.post(`${apiBase()}/contacts`, payload);
        setToast({ type: "success", msg: "Contact added." });
      }
      setDialogOpen(false);
      await Promise.all([loadContacts(), loadGroups()]);
    } catch (error) {
      setToast({
        type: "error",
        msg: error?.response?.data?.message || "Failed to save contact.",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (contact) => {
    const ok = window.confirm(`Remove ${contact.email} from the list?`);
    if (!ok) return;
    try {
      await axios.delete(`${apiBase()}/contacts/${contact.id}`);
      setToast({ type: "success", msg: "Contact removed." });
      await Promise.all([loadContacts(), loadGroups()]);
    } catch (error) {
      setToast({
        type: "error",
        msg: error?.response?.data?.message || "Failed to remove contact.",
      });
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  return (
    <div className="min-h-screen">
      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setToast(null)}
      >
        {toast ? (
          <Alert
            severity={toast.type}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      <div className="p-10 max-md:p-5 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <IconButton onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <div className="text-black font-semibold text-lg">Internal List</div>
        </div>
        <Button
          onClick={openAdd}
          sx={{
            backgroundColor: BRAND,
            color: "white",
            "&:hover": { backgroundColor: BRAND, color: "white" },
          }}
        >
          Add Contact
        </Button>
      </div>

      <div className="px-10 max-md:px-5 pb-10">
        <div className="flex gap-3 mb-4 flex-wrap">
          <TextField
            size="small"
            placeholder="Search by email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Group"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All groups</MenuItem>
            {groups.map((g) => (
              <MenuItem key={g} value={g} className="capitalize">
                {g}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {loading ? (
          <div className="flex justify-center mt-10">
            <CircularProgress style={{ color: BRAND }} />
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex justify-center text-black mt-10">
            No contacts found.
          </div>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f7f9" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Group</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Added</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>
                      {c.group ? (
                        <Chip
                          label={c.group}
                          size="small"
                          className="capitalize"
                          sx={{ backgroundColor: "#e6f7f0", color: BRAND }}
                        />
                      ) : (
                        <span className="text-gray-400">No group</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(c.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(c)}>
                        <EditIcon fontSize="small" sx={{ color: BRAND }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => deleteContact(c)}>
                        <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            sx={{ mb: 3, mt: 1 }}
            error={formEmail !== "" && !emailRegex.test(formEmail.trim())}
            helperText={
              formEmail !== "" && !emailRegex.test(formEmail.trim())
                ? "Invalid email format"
                : " "
            }
          />
          <Autocomplete
            freeSolo
            options={groups}
            value={formGroup}
            onChange={(_e, val) => setFormGroup(val || "")}
            onInputChange={(_e, val) => setFormGroup(val || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Group"
                placeholder="Select existing or type a new group"
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ color: "#666" }}>
            Cancel
          </Button>
          <Button
            onClick={saveContact}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: BRAND,
              "&:hover": { backgroundColor: BRAND },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : editing ? (
              "Save"
            ) : (
              "Add"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
